import { 
  Calendar as CalendarIcon, Users, Clock, 
  Briefcase, Truck, Phone, Target, Bot, Lock,
  Check, XCircle, HelpCircle, Bell,
  UserCircle, DollarSign, Mail, MessageSquare, Megaphone,
  Mountain, ListTodo, AlertCircle
} from 'lucide-react';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// User color palette for multi-user view
export const USER_COLORS = [
  { color: '#3b82f6', lightColor: '#dbeafe', name: 'Blue' },
  { color: '#10b981', lightColor: '#d1fae5', name: 'Green' },
  { color: '#f59e0b', lightColor: '#fef3c7', name: 'Amber' },
  { color: '#8b5cf6', lightColor: '#ede9fe', name: 'Purple' },
  { color: '#ec4899', lightColor: '#fce7f3', name: 'Pink' },
  { color: '#ef4444', lightColor: '#fee2e2', name: 'Red' },
  { color: '#06b6d4', lightColor: '#cffafe', name: 'Cyan' },
  { color: '#84cc16', lightColor: '#ecfccb', name: 'Lime' },
  { color: '#f97316', lightColor: '#ffedd5', name: 'Orange' },
  { color: '#6366f1', lightColor: '#e0e7ff', name: 'Indigo' },
];

// Get user color based on index or user ID
export const getUserColor = (userId, userIndex = 0) => {
  if (!userId) return USER_COLORS[0];
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return USER_COLORS[hash % USER_COLORS.length];
};

// Event type configurations with icons and colors
export const EVENT_TYPES = {
  meeting: { label: 'Meeting', icon: Users, color: '#3b82f6', lightColor: '#dbeafe' },
  sales_appointment: { label: 'Sales Appointment', icon: Briefcase, color: '#10b981', lightColor: '#d1fae5' },
  job_site_visit: { label: 'Job Site Visit', icon: Truck, color: '#f59e0b', lightColor: '#fef3c7' },
  crew_schedule: { label: 'Crew Schedule', icon: Users, color: '#8b5cf6', lightColor: '#ede9fe' },
  call_center_shift: { label: 'Call Center Shift', icon: Phone, color: '#ec4899', lightColor: '#fce7f3' },
  eos_meeting: { label: 'EOS Meeting', icon: Target, color: '#ef4444', lightColor: '#fee2e2' },
  task_deadline: { label: 'Task Deadline', icon: Clock, color: '#6366f1', lightColor: '#e0e7ff' },
  ai_generated: { label: 'AI Generated', icon: Bot, color: '#14b8a6', lightColor: '#ccfbf1' },
  personal: { label: 'Personal', icon: Lock, color: '#64748b', lightColor: '#f1f5f9' },
  other: { label: 'Other', icon: CalendarIcon, color: '#71717a', lightColor: '#f4f4f5' }
};

// RSVP status configurations
export const RSVP_STATUS = {
  accepted: { label: 'Accepted', icon: Check, color: 'text-green-600', bg: 'bg-green-100' },
  declined: { label: 'Declined', icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
  tentative: { label: 'Tentative', icon: HelpCircle, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  pending: { label: 'Pending', icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100' }
};

// Working hours configuration (visual distinction only - all 24 hours are shown)
export const WORK_HOURS = { start: 8, end: 18 };
export const CALENDAR_HOURS = { start: 0, end: 24 }; // Full 24 hours
export const HOUR_HEIGHT = 60; // pixels per hour
export const MIN_EVENT_HEIGHT = 30; // minimum 30 minutes

// Reminder options configuration
export const REMINDER_OPTIONS = [
  { value: 0, label: 'At time of event' },
  { value: 5, label: '5 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
  { value: 2880, label: '2 days before' },
  { value: 10080, label: '1 week before' },
];

// Notification types
export const NOTIFICATION_TYPES = {
  reminder: { icon: Bell, color: 'text-blue-500', bg: 'bg-blue-100' },
  event_update: { icon: CalendarIcon, color: 'text-green-500', bg: 'bg-green-100' },
  event_cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100' },
  new_invite: { icon: Users, color: 'text-purple-500', bg: 'bg-purple-100' },
  rsvp_response: { icon: Check, color: 'text-teal-500', bg: 'bg-teal-100' },
};

// Platform linkable entity types configuration
export const LINKABLE_ENTITY_TYPES = {
  job: { 
    label: 'Jobs', 
    icon: Briefcase, 
    color: '#f59e0b', 
    bg: 'bg-amber-100',
    fieldSingle: 'related_job_id',
    fieldMultiple: 'related_job_ids',
    nameSingle: 'related_job_name',
    nameMultiple: 'related_job_names'
  },
  contact: { 
    label: 'Contacts', 
    icon: UserCircle, 
    color: '#3b82f6', 
    bg: 'bg-blue-100',
    fieldSingle: 'related_contact_id',
    fieldMultiple: 'related_contact_ids',
    nameSingle: 'related_contact_name',
    nameMultiple: 'related_contact_names'
  },
  lead: { 
    label: 'Leads (Sales)', 
    icon: DollarSign, 
    color: '#10b981', 
    bg: 'bg-green-100',
    fieldSingle: 'related_lead_id',
    fieldMultiple: 'related_lead_ids',
    nameSingle: 'related_lead_name',
    nameMultiple: 'related_lead_names'
  },
  eos_meeting: { 
    label: 'EOS Meetings', 
    icon: Target, 
    color: '#ef4444', 
    bg: 'bg-red-100',
    fieldSingle: 'related_eos_meeting_id',
    fieldMultiple: 'related_eos_meeting_ids',
    nameSingle: 'related_eos_meeting_name',
    nameMultiple: 'related_eos_meeting_names'
  },
  eos_rock: { 
    label: 'EOS Rocks', 
    icon: Mountain, 
    color: '#8b5cf6', 
    bg: 'bg-purple-100',
    fieldSingle: 'related_eos_rock_id',
    fieldMultiple: 'related_eos_rock_ids',
    nameSingle: 'related_eos_rock_name',
    nameMultiple: 'related_eos_rock_names'
  },
  eos_todo: { 
    label: 'EOS Todos', 
    icon: ListTodo, 
    color: '#06b6d4', 
    bg: 'bg-cyan-100',
    fieldSingle: 'related_eos_todo_id',
    fieldMultiple: 'related_eos_todo_ids',
    nameSingle: 'related_eos_todo_name',
    nameMultiple: 'related_eos_todo_names'
  },
  eos_issue: { 
    label: 'EOS Issues', 
    icon: AlertCircle, 
    color: '#f97316', 
    bg: 'bg-orange-100',
    fieldSingle: 'related_eos_issue_id',
    fieldMultiple: 'related_eos_issue_ids',
    nameSingle: 'related_eos_issue_name',
    nameMultiple: 'related_eos_issue_names'
  },
  communication: { 
    label: 'Communications', 
    icon: Mail, 
    color: '#ec4899', 
    bg: 'bg-pink-100',
    fieldSingle: 'related_communication_id',
    fieldMultiple: 'related_communication_ids',
    nameSingle: 'related_communication_name',
    nameMultiple: 'related_communication_names'
  },
  call: { 
    label: 'Calls', 
    icon: Phone, 
    color: '#14b8a6', 
    bg: 'bg-teal-100',
    fieldSingle: 'related_call_id',
    fieldMultiple: 'related_call_ids',
    nameSingle: 'related_call_name',
    nameMultiple: 'related_call_names'
  },
  chat: { 
    label: 'Chats', 
    icon: MessageSquare, 
    color: '#6366f1', 
    bg: 'bg-indigo-100',
    fieldSingle: 'related_chat_id',
    fieldMultiple: 'related_chat_ids',
    nameSingle: 'related_chat_name',
    nameMultiple: 'related_chat_names'
  },
  campaign: { 
    label: 'Marketing', 
    icon: Megaphone, 
    color: '#84cc16', 
    bg: 'bg-lime-100',
    fieldSingle: 'related_marketing_campaign_id',
    fieldMultiple: 'related_marketing_campaign_ids',
    nameSingle: 'related_marketing_campaign_name',
    nameMultiple: 'related_marketing_campaign_names'
  }
};

// Default event form state
export const getDefaultEventForm = (defaultReminder = 15) => ({
  title: '',
  description: '',
  start_date: '',
  end_date: '',
  all_day: false,
  location: '',
  video_link: '',
  event_type: 'meeting',
  priority: 'medium',
  color: '#3b82f6',
  attendees: [],
  // Platform Integration - linked entities
  related_job_id: '',
  related_job_ids: [],
  related_job_names: [],
  related_contact_id: '',
  related_contact_ids: [],
  related_contact_names: [],
  related_lead_id: '',
  related_lead_ids: [],
  related_lead_names: [],
  related_eos_meeting_id: '',
  related_eos_meeting_ids: [],
  related_eos_meeting_names: [],
  related_eos_rock_id: '',
  related_eos_rock_ids: [],
  related_eos_rock_names: [],
  related_eos_todo_id: '',
  related_eos_todo_ids: [],
  related_eos_todo_names: [],
  related_eos_issue_id: '',
  related_eos_issue_ids: [],
  related_eos_issue_names: [],
  related_communication_id: '',
  related_communication_ids: [],
  related_communication_names: [],
  related_call_id: '',
  related_call_ids: [],
  related_call_names: [],
  related_chat_id: '',
  related_chat_ids: [],
  related_chat_names: [],
  related_marketing_campaign_id: '',
  related_marketing_campaign_ids: [],
  related_marketing_campaign_names: [],
  is_public: false,
  rsvp_required: false,
  attachments: [],
  reminders: [defaultReminder]
});

// Populate event form from existing event
export const populateEventForm = (event) => ({
  title: event.title,
  description: event.description || '',
  start_date: new Date(event.start_date).toISOString().slice(0, 16),
  end_date: new Date(event.end_date).toISOString().slice(0, 16),
  all_day: event.all_day || false,
  location: event.location || '',
  video_link: event.video_link || '',
  event_type: event.event_type || 'meeting',
  priority: event.priority || 'medium',
  color: event.color || '#3b82f6',
  attendees: event.attendees || [],
  // Platform Integration - linked entities
  related_job_id: event.related_job_id || '',
  related_job_ids: event.related_job_ids || [],
  related_job_names: event.related_job_names || [],
  related_contact_id: event.related_contact_id || '',
  related_contact_ids: event.related_contact_ids || [],
  related_contact_names: event.related_contact_names || [],
  related_lead_id: event.related_lead_id || '',
  related_lead_ids: event.related_lead_ids || [],
  related_lead_names: event.related_lead_names || [],
  related_eos_meeting_id: event.related_eos_meeting_id || '',
  related_eos_meeting_ids: event.related_eos_meeting_ids || [],
  related_eos_meeting_names: event.related_eos_meeting_names || [],
  related_eos_rock_id: event.related_eos_rock_id || '',
  related_eos_rock_ids: event.related_eos_rock_ids || [],
  related_eos_rock_names: event.related_eos_rock_names || [],
  related_eos_todo_id: event.related_eos_todo_id || '',
  related_eos_todo_ids: event.related_eos_todo_ids || [],
  related_eos_todo_names: event.related_eos_todo_names || [],
  related_eos_issue_id: event.related_eos_issue_id || '',
  related_eos_issue_ids: event.related_eos_issue_ids || [],
  related_eos_issue_names: event.related_eos_issue_names || [],
  related_communication_id: event.related_communication_id || '',
  related_communication_ids: event.related_communication_ids || [],
  related_communication_names: event.related_communication_names || [],
  related_call_id: event.related_call_id || '',
  related_call_ids: event.related_call_ids || [],
  related_call_names: event.related_call_names || [],
  related_chat_id: event.related_chat_id || '',
  related_chat_ids: event.related_chat_ids || [],
  related_chat_names: event.related_chat_names || [],
  related_marketing_campaign_id: event.related_marketing_campaign_id || '',
  related_marketing_campaign_ids: event.related_marketing_campaign_ids || [],
  related_marketing_campaign_names: event.related_marketing_campaign_names || [],
  is_public: event.is_public || false,
  rsvp_required: event.rsvp_required || false,
  attachments: event.attachments || []
});